import {
    IPublicClientApplication,
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
    BrowserAuthError,
} from '@azure/msal-browser';
import Configuration from '../config/einvoicing-configuration';

const MSAL_KEY_PREFIX = 'msal.';

// One-shot flag (per tab) that records we have already cleared MSAL state and
// forced a re-authentication. It prevents an infinite reload/redirect loop when
// the backend keeps rejecting an otherwise-valid token (e.g. a genuine 403
// surfaced as 401). It is cleared on the first successful API response.
const REAUTH_FLAG = 'einv.reauthAttempted';

// Module-level guard so that only a single interactive redirect is triggered
// even when multiple components request a token concurrently on page load.
// The MSAL cache lives in localStorage (to persist across CDN/Front Door
// redirects), which means a stray `interaction_in_progress` lock survives a
// page reload. Racing multiple acquireTokenRedirect calls is what left that
// lock stuck and forced users to logout/login, so we serialize the redirect.
let redirectInProgress = false;

/**
 * Removes all MSAL cache entries from both localStorage and sessionStorage.
 * Used to recover from a stuck interaction/auth state.
 */
export function clearMsalState(): void {
    [localStorage, sessionStorage].forEach((store) => {
        Object.keys(store)
            .filter((key) => key.startsWith(MSAL_KEY_PREFIX))
            .forEach((key) => store.removeItem(key));
    });
}

/**
 * Clears the one-shot re-auth guard. Call this after a successful authenticated
 * request so that a later token expiry can recover again.
 */
export function clearReauthFlag(): void {
    sessionStorage.removeItem(REAUTH_FLAG);
}

/**
 * Recovers from an unauthorized (401) API response caused by a missing, expired
 * or stuck token. Clears MSAL state and reloads so that MsalAuthenticationTemplate
 * performs a fresh interactive login. Guarded so it runs at most once per tab —
 * if the backend still rejects the fresh token, the caller should surface the
 * normal error page instead of looping.
 *
 * @returns true if recovery was triggered (page is reloading); false if recovery
 *          was already attempted and the caller should show the error page.
 */
export function recoverFromUnauthorized(): boolean {
    if (sessionStorage.getItem(REAUTH_FLAG) === 'true') {
        return false;
    }
    sessionStorage.setItem(REAUTH_FLAG, 'true');
    clearMsalState();
    window.location.reload();
    return true;
}

/**
 * Recovery action for the "Refresh page" button on the error state. A plain
 * window.location.reload() reloads into the same expired/stuck token state,
 * which is why the only reliable fix was a manual logout/login. This clears the
 * MSAL cache and the one-shot re-auth guard first, so the reload performs a
 * fresh silent (or interactive) sign-in — the same effect as logout/login, in a
 * single click.
 */
export function forceReauthReload(): void {
    // Intentionally logged so we can confirm (via the browser console with "Preserve log"
    // enabled, since the reload clears it) that the Refresh button ran the recovery path
    // rather than a bare reload.
    console.info('[einv][auth] forceReauthReload: clearing MSAL cache + one-shot reauth guard, then reloading to re-authenticate.');
    clearReauthFlag();
    clearMsalState();
    window.location.reload();
}

/**
 * Acquires an ID token silently. If interaction is required, a single redirect
 * is triggered across the whole app (guarded against concurrent callers). When
 * another interaction already holds the MSAL lock, an empty string is returned
 * instead of throwing so that callers do not surface a generic page error.
 */
export async function acquireAccessToken(
    instance: IPublicClientApplication,
    account?: AccountInfo
): Promise<string> {
    const request = { ...Configuration.msalTokenRequest, account };
    try {
        const result: AuthenticationResult = await instance.acquireTokenSilent(request);
        return result.idToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            if (!redirectInProgress) {
                redirectInProgress = true;
                await instance.acquireTokenRedirect(request);
            }
            return '';
        }
        // An interaction holds the MSAL lock. Only swallow this when we know we
        // triggered the redirect in this module — the in-flight interaction will
        // resolve auth. Otherwise it's a stale persisted lock (localStorage cache
        // survives reloads) with no redirect to resolve it, so recover by clearing
        // MSAL state and starting a fresh redirect.
        if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
            if (redirectInProgress) {
                return '';
            }
            clearMsalState();
            redirectInProgress = true;
            await instance.acquireTokenRedirect(request);
            return '';
        }
        throw error;
    }
}
